import { Guid } from "@common/guid";
import { IncidentLogFieldOrderItem } from "./IncidentLogs.types";

// Mirror of Backend/Core/Filtering/IncidentLogs/IncidentLogColumn.cs. Values are explicit and must stay in step:
// they travel in the search request, in sortByColumn and in the saved-view blobs the backend serves.
export enum IncidentLogColumn {
    UserFriendlyId = 0,
    Name = 1,
    Status = 2,
    Severity = 3,
    Types = 4,
    StartDate = 5,
    DiscoveryDate = 6,
    EndDate = 7,
    Responsible = 8,
    GroupEntities = 9,
    ReportedToAgency = 10,
    AffectedPeopleNotified = 11,
    DataCategories = 12,
    DataSubjects = 13,
    Tags = 14,
    EstimatedNumberOfAffectedPeople = 15,
    CreatedAt = 16,
    BusinessAreas = 17,
    ComplianceAreas = 18,
    Template = 19,
    ReportedToAgencyDate = 20,
    AffectedPeopleNotifiedDate = 21,
    AccessRequestType = 22,
    Evaluation = 23,
    Note = 24,
    Description = 25,
    ReportedBy = 26,
    Consequences = 27,
    Measures = 28,
    ReportedToAgencyDescription = 29,
    AffectedPeopleNotifiedDescription = 30,
    VisibleToAllUsers = 31,
    AdditionalUsers = 32,
    ExcludedUsers = 33,
    AccessBusinessAreas = 34,
    SuspectedUnlawfulOrMaliciousAct = 35,
    EstimatedNumberOfAffectedServiceRecipients = 36,
    EstimatedFinancialLoss = 37,
    CrossBorderImpact = 38,
    AffectedCountries = 39,
    AffectedServices = 40,
    ReceiptDate = 41,
    ReceivedBy = 42,
    ReceiptChannel = 43,
    DeadlineExtended = 44,
    DeadlineExtensionJustification = 45,
    DataSubjectInformedOfExtensionDate = 46,
    DataSubjectName = 47,
    IdentityVerified = 48,
    IdentityVerificationMethod = 49,
    RequestMadeThroughRepresentative = 50,
    RequestedDataCategories = 51,
    AccessRequestOutcome = 52,
    AccessRequestOutcomeJustification = 53,
    ResponseSent = 54,
    ResponseDate = 55,
    ResponseMethod = 56,
    CopyOfPersonalDataProvided = 57,
    InformedOfRightToComplain = 58,
    SignificantIncidentAwarenessDate = 59,
    ReportedToAuthorities = 60,
    EarlyWarningDate = 61,
    IncidentNotificationDate = 62,
    FinalReportDate = 63,
    ReportedToAuthoritiesDescription = 64,
    ServiceRecipientsNotified = 65,
    ServiceRecipientsNotifiedDate = 66,
    ServiceRecipientsNotifiedDescription = 67,
    RecoveryDate = 68,
    AffectedSecurityProperties = 69,
    PersonalDataInvolved = 70,
    InformationSecurityReportedToAuthorities = 71,
    InformationSecurityReportedToAuthoritiesDate = 72,
    InformationSecurityReportedToAuthoritiesDescription = 73,
    AffectedPartiesNotified = 74,
    AffectedPartiesNotifiedDate = 75,
    AffectedPartiesNotifiedDescription = 76,
    SupplierNotificationDate = 77,
    RepresentativeName = 78,
    EstimatedNumberOfAffectedRecords = 79,
}

// The string ids the column picker, the table headers and user-saved views work in.
export const IncidentLogColumnId = {
    userFriendlyId: "userFriendlyId",
    name: "name",
    status: "status",
    severity: "severity",
    types: "types",
    startDate: "startDate",
    discoveryDate: "discoveryDate",
    receiptDate: "receiptDate",
    significantIncidentAwarenessDate: "significantIncidentAwarenessDate",
    endDate: "endDate",
    recoveryDate: "recoveryDate",
    supplierNotificationDate: "supplierNotificationDate",
    responsible: "responsible",
    groupEntities: "groupEntities",
    reportedToAgency: "reportedToAgency",
    affectedPeopleNotified: "affectedPeopleNotified",
    dataCategories: "dataCategories",
    dataSubjects: "dataSubjects",
    tags: "tags",
    estimatedNumberOfAffectedPeople: "estimatedNumberOfAffectedPeople",
    estimatedNumberOfAffectedRecords: "estimatedNumberOfAffectedRecords",
    affectedServices: "affectedServices",
    affectedSecurityProperties: "affectedSecurityProperties",
    personalDataInvolved: "personalDataInvolved",
    estimatedNumberOfAffectedServiceRecipients: "estimatedNumberOfAffectedServiceRecipients",
    estimatedFinancialLoss: "estimatedFinancialLoss",
    crossBorderImpact: "crossBorderImpact",
    affectedCountries: "affectedCountries",
    createdAt: "createdAt",
    businessAreas: "businessAreas",
    complianceAreas: "complianceAreas",
    template: "template",
    reportedToAgencyDate: "reportedToAgencyDate",
    affectedPeopleNotifiedDate: "affectedPeopleNotifiedDate",
    accessRequestType: "accessRequestType",
    suspectedUnlawfulOrMaliciousAct: "suspectedUnlawfulOrMaliciousAct",
    description: "description",
    note: "note",
    reportedBy: "reportedBy",
    receivedBy: "receivedBy",
    receiptChannel: "receiptChannel",
    deadlineExtended: "deadlineExtended",
    deadlineExtensionJustification: "deadlineExtensionJustification",
    dataSubjectInformedOfExtensionDate: "dataSubjectInformedOfExtensionDate",
    dataSubjectName: "dataSubjectName",
    identityVerified: "identityVerified",
    identityVerificationMethod: "identityVerificationMethod",
    representativeName: "representativeName",
    requestMadeThroughRepresentative: "requestMadeThroughRepresentative",
    requestedDataCategories: "requestedDataCategories",
    accessRequestOutcome: "accessRequestOutcome",
    accessRequestOutcomeJustification: "accessRequestOutcomeJustification",
    responseSent: "responseSent",
    responseDate: "responseDate",
    responseMethod: "responseMethod",
    copyOfPersonalDataProvided: "copyOfPersonalDataProvided",
    informedOfRightToComplain: "informedOfRightToComplain",
    consequences: "consequences",
    measures: "measures",
    reportedToAgencyDescription: "reportedToAgencyDescription",
    affectedPeopleNotifiedDescription: "affectedPeopleNotifiedDescription",
    reportedToAuthorities: "reportedToAuthorities",
    earlyWarningDate: "earlyWarningDate",
    incidentNotificationDate: "incidentNotificationDate",
    finalReportDate: "finalReportDate",
    reportedToAuthoritiesDescription: "reportedToAuthoritiesDescription",
    serviceRecipientsNotified: "serviceRecipientsNotified",
    serviceRecipientsNotifiedDate: "serviceRecipientsNotifiedDate",
    serviceRecipientsNotifiedDescription: "serviceRecipientsNotifiedDescription",
    informationSecurityReportedToAuthorities: "informationSecurityReportedToAuthorities",
    informationSecurityReportedToAuthoritiesDate: "informationSecurityReportedToAuthoritiesDate",
    informationSecurityReportedToAuthoritiesDescription: "informationSecurityReportedToAuthoritiesDescription",
    affectedPartiesNotified: "affectedPartiesNotified",
    affectedPartiesNotifiedDate: "affectedPartiesNotifiedDate",
    affectedPartiesNotifiedDescription: "affectedPartiesNotifiedDescription",
    visibleToAllUsers: "visibleToAllUsers",
    additionalUsers: "additionalUsers",
    excludedUsers: "excludedUsers",
    accessBusinessAreas: "accessBusinessAreas",
    evaluation: "evaluation",
} as const;

export const incidentLogColumnByFilterId: Record<string, IncidentLogColumn> = {
    [IncidentLogColumnId.userFriendlyId]: IncidentLogColumn.UserFriendlyId,
    [IncidentLogColumnId.name]: IncidentLogColumn.Name,
    [IncidentLogColumnId.status]: IncidentLogColumn.Status,
    [IncidentLogColumnId.severity]: IncidentLogColumn.Severity,
    [IncidentLogColumnId.types]: IncidentLogColumn.Types,
    [IncidentLogColumnId.startDate]: IncidentLogColumn.StartDate,
    [IncidentLogColumnId.discoveryDate]: IncidentLogColumn.DiscoveryDate,
    [IncidentLogColumnId.receiptDate]: IncidentLogColumn.ReceiptDate,
    [IncidentLogColumnId.significantIncidentAwarenessDate]: IncidentLogColumn.SignificantIncidentAwarenessDate,
    [IncidentLogColumnId.endDate]: IncidentLogColumn.EndDate,
    [IncidentLogColumnId.recoveryDate]: IncidentLogColumn.RecoveryDate,
    [IncidentLogColumnId.supplierNotificationDate]: IncidentLogColumn.SupplierNotificationDate,
    [IncidentLogColumnId.responsible]: IncidentLogColumn.Responsible,
    [IncidentLogColumnId.groupEntities]: IncidentLogColumn.GroupEntities,
    [IncidentLogColumnId.reportedToAgency]: IncidentLogColumn.ReportedToAgency,
    [IncidentLogColumnId.affectedPeopleNotified]: IncidentLogColumn.AffectedPeopleNotified,
    [IncidentLogColumnId.dataCategories]: IncidentLogColumn.DataCategories,
    [IncidentLogColumnId.dataSubjects]: IncidentLogColumn.DataSubjects,
    [IncidentLogColumnId.tags]: IncidentLogColumn.Tags,
    [IncidentLogColumnId.estimatedNumberOfAffectedPeople]: IncidentLogColumn.EstimatedNumberOfAffectedPeople,
    [IncidentLogColumnId.estimatedNumberOfAffectedRecords]: IncidentLogColumn.EstimatedNumberOfAffectedRecords,
    [IncidentLogColumnId.affectedServices]: IncidentLogColumn.AffectedServices,
    [IncidentLogColumnId.affectedSecurityProperties]: IncidentLogColumn.AffectedSecurityProperties,
    [IncidentLogColumnId.personalDataInvolved]: IncidentLogColumn.PersonalDataInvolved,
    [IncidentLogColumnId.estimatedNumberOfAffectedServiceRecipients]: IncidentLogColumn.EstimatedNumberOfAffectedServiceRecipients,
    [IncidentLogColumnId.estimatedFinancialLoss]: IncidentLogColumn.EstimatedFinancialLoss,
    [IncidentLogColumnId.crossBorderImpact]: IncidentLogColumn.CrossBorderImpact,
    [IncidentLogColumnId.affectedCountries]: IncidentLogColumn.AffectedCountries,
    [IncidentLogColumnId.createdAt]: IncidentLogColumn.CreatedAt,
    [IncidentLogColumnId.businessAreas]: IncidentLogColumn.BusinessAreas,
    [IncidentLogColumnId.complianceAreas]: IncidentLogColumn.ComplianceAreas,
    [IncidentLogColumnId.template]: IncidentLogColumn.Template,
    [IncidentLogColumnId.reportedToAgencyDate]: IncidentLogColumn.ReportedToAgencyDate,
    [IncidentLogColumnId.affectedPeopleNotifiedDate]: IncidentLogColumn.AffectedPeopleNotifiedDate,
    [IncidentLogColumnId.accessRequestType]: IncidentLogColumn.AccessRequestType,
    [IncidentLogColumnId.suspectedUnlawfulOrMaliciousAct]: IncidentLogColumn.SuspectedUnlawfulOrMaliciousAct,
    [IncidentLogColumnId.description]: IncidentLogColumn.Description,
    [IncidentLogColumnId.note]: IncidentLogColumn.Note,
    [IncidentLogColumnId.reportedBy]: IncidentLogColumn.ReportedBy,
    [IncidentLogColumnId.receivedBy]: IncidentLogColumn.ReceivedBy,
    [IncidentLogColumnId.receiptChannel]: IncidentLogColumn.ReceiptChannel,
    [IncidentLogColumnId.deadlineExtended]: IncidentLogColumn.DeadlineExtended,
    [IncidentLogColumnId.deadlineExtensionJustification]: IncidentLogColumn.DeadlineExtensionJustification,
    [IncidentLogColumnId.dataSubjectInformedOfExtensionDate]: IncidentLogColumn.DataSubjectInformedOfExtensionDate,
    [IncidentLogColumnId.dataSubjectName]: IncidentLogColumn.DataSubjectName,
    [IncidentLogColumnId.identityVerified]: IncidentLogColumn.IdentityVerified,
    [IncidentLogColumnId.identityVerificationMethod]: IncidentLogColumn.IdentityVerificationMethod,
    [IncidentLogColumnId.representativeName]: IncidentLogColumn.RepresentativeName,
    [IncidentLogColumnId.requestMadeThroughRepresentative]: IncidentLogColumn.RequestMadeThroughRepresentative,
    [IncidentLogColumnId.requestedDataCategories]: IncidentLogColumn.RequestedDataCategories,
    [IncidentLogColumnId.accessRequestOutcome]: IncidentLogColumn.AccessRequestOutcome,
    [IncidentLogColumnId.accessRequestOutcomeJustification]: IncidentLogColumn.AccessRequestOutcomeJustification,
    [IncidentLogColumnId.responseSent]: IncidentLogColumn.ResponseSent,
    [IncidentLogColumnId.responseDate]: IncidentLogColumn.ResponseDate,
    [IncidentLogColumnId.responseMethod]: IncidentLogColumn.ResponseMethod,
    [IncidentLogColumnId.copyOfPersonalDataProvided]: IncidentLogColumn.CopyOfPersonalDataProvided,
    [IncidentLogColumnId.informedOfRightToComplain]: IncidentLogColumn.InformedOfRightToComplain,
    [IncidentLogColumnId.consequences]: IncidentLogColumn.Consequences,
    [IncidentLogColumnId.measures]: IncidentLogColumn.Measures,
    [IncidentLogColumnId.reportedToAgencyDescription]: IncidentLogColumn.ReportedToAgencyDescription,
    [IncidentLogColumnId.affectedPeopleNotifiedDescription]: IncidentLogColumn.AffectedPeopleNotifiedDescription,
    [IncidentLogColumnId.reportedToAuthorities]: IncidentLogColumn.ReportedToAuthorities,
    [IncidentLogColumnId.earlyWarningDate]: IncidentLogColumn.EarlyWarningDate,
    [IncidentLogColumnId.incidentNotificationDate]: IncidentLogColumn.IncidentNotificationDate,
    [IncidentLogColumnId.finalReportDate]: IncidentLogColumn.FinalReportDate,
    [IncidentLogColumnId.reportedToAuthoritiesDescription]: IncidentLogColumn.ReportedToAuthoritiesDescription,
    [IncidentLogColumnId.serviceRecipientsNotified]: IncidentLogColumn.ServiceRecipientsNotified,
    [IncidentLogColumnId.serviceRecipientsNotifiedDate]: IncidentLogColumn.ServiceRecipientsNotifiedDate,
    [IncidentLogColumnId.serviceRecipientsNotifiedDescription]: IncidentLogColumn.ServiceRecipientsNotifiedDescription,
    [IncidentLogColumnId.informationSecurityReportedToAuthorities]: IncidentLogColumn.InformationSecurityReportedToAuthorities,
    [IncidentLogColumnId.informationSecurityReportedToAuthoritiesDate]: IncidentLogColumn.InformationSecurityReportedToAuthoritiesDate,
    [IncidentLogColumnId.informationSecurityReportedToAuthoritiesDescription]: IncidentLogColumn.InformationSecurityReportedToAuthoritiesDescription,
    [IncidentLogColumnId.affectedPartiesNotified]: IncidentLogColumn.AffectedPartiesNotified,
    [IncidentLogColumnId.affectedPartiesNotifiedDate]: IncidentLogColumn.AffectedPartiesNotifiedDate,
    [IncidentLogColumnId.affectedPartiesNotifiedDescription]: IncidentLogColumn.AffectedPartiesNotifiedDescription,
    [IncidentLogColumnId.visibleToAllUsers]: IncidentLogColumn.VisibleToAllUsers,
    [IncidentLogColumnId.additionalUsers]: IncidentLogColumn.AdditionalUsers,
    [IncidentLogColumnId.excludedUsers]: IncidentLogColumn.ExcludedUsers,
    [IncidentLogColumnId.accessBusinessAreas]: IncidentLogColumn.AccessBusinessAreas,
    [IncidentLogColumnId.evaluation]: IncidentLogColumn.Evaluation,
};

// Derived from the map above rather than written out a second time, so the two cannot fall out of step.
const incidentLogFilterIdByColumn: Record<number, string> = Object.fromEntries(
    Object.entries(incidentLogColumnByFilterId).map(([filterId, column]) => [column, filterId])
);

export function getIncidentLogFilterIdByColumn(column: IncidentLogColumn): string | undefined {
    return incidentLogFilterIdByColumn[column];
}

// A display field id is either one of the names above or the bare guid of a template custom field.
export function toIncidentLogDisplayFields(filterIds: Array<string>): Array<IncidentLogFieldOrderItem> {
    return filterIds
        .map((filterId, index) =>
            Guid.isGuid(filterId) ? { customFieldId: filterId, displayOrder: index } : { column: incidentLogColumnByFilterId[filterId], displayOrder: index }
        )
        .filter((field) => field.customFieldId !== undefined || field.column !== undefined);
}
